import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { isBrowserSupported } from '@sm/utils';
import { Modal, ModalBody, ModalHeader } from '@sm/wds-react';
import { defineMessages, T, t } from '@sm/intl';

const COPY = defineMessages({
  DIALOG_HEADER: {
    id: 'BrowserGuard.DialogHeader',
    defaultMessage: "Your browser isn't supported",
    description:
      'Header of a dialog displaying a message to the users that their browser is not supported.',
  },
  DIALOG_BODY: {
    id: 'BrowserGuard.DialogBody',
    defaultMessage: 'For more information please visit:',
    description:
      'Text describing that more information on the issue will be available on the following link',
  },
  LINK_TEXT: {
    id: 'BrowserGuard.LinkText',
    defaultMessage: 'Supported Browsers',
    description:
      'Text for a link directing the user to the Supported Browsers help page',
  },
});

const BrowserGuardDialog = () => {
  return (
    <div data-testid="BrowserGuardDialog">
      <Modal show dismissible>
        <ModalHeader header={t(COPY.DIALOG_HEADER)} />
        <ModalBody>
          <p>
            <T desc={COPY.DIALOG_BODY} />
          </p>
          <p>
            <a href="https://help.surveymonkey.com/articles/en_US/kb/What-browser-versions-do-you-support">
              <T desc={COPY.LINK_TEXT} />
            </a>
          </p>
        </ModalBody>
      </Modal>
    </div>
  );
};

/**
 * Component for checking if the user browser is supported
 *
 * Basic usage example:
 * `<BrowserGuard />`
 *
 * The basic usage will check against the default supported browsers
 * as documented here: https://help.surveymonkey.com/articles/en_US/kb/What-browser-versions-do-you-support
 *
 * If the user's browser is not supported a modal window will be displayed to the user.
 *
 * Optionally one can provide a list of supported browsers via the supportedBrowsers prop.
 * Also, a different UI may be rendered if a children is passed to the component.
 */
const BrowserGuard = ({ children, supportedBrowsers }) => {
  const [hasVerified, setHasVerified] = useState(false);
  const [isSupported, setIsSupported] = useState(false);

  useEffect(() => {
    if (window && !hasVerified) {
      setIsSupported(
        isBrowserSupported(window.navigator.userAgent, supportedBrowsers)
      );
      setHasVerified(true);
    }
  }, [hasVerified, supportedBrowsers]);

  return (
    <Choose>
      <When condition={hasVerified && !isSupported}>
        <div data-testid="BrowserGuard">{children}</div>
      </When>
      <Otherwise>{null}</Otherwise>
    </Choose>
  );
};

BrowserGuard.propTypes = {
  children: PropTypes.element,
  supportedBrowsers: PropTypes.arrayOf(
    PropTypes.shape({
      vendor: PropTypes.oneOf(['chrome', 'firefox', 'safari', 'ie', 'edge'])
        .isRequired,
      operator: PropTypes.oneOf(['>', '<', '~', '=', '>=', '<=']).isRequired,
      version: PropTypes.string.isRequired,
    })
  ),
};

BrowserGuard.defaultProps = {
  children: <BrowserGuardDialog />,
  supportedBrowsers: undefined,
};

export default BrowserGuard;
